import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper,
  Button,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Collapse,
  IconButton,
  Grid,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText
} from '@mui/material';
import PatientEditForm from './PatientEditForm';
import NoteIcon from '@mui/icons-material/Note';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PersonIcon from '@mui/icons-material/Person';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DraftsIcon from '@mui/icons-material/Drafts';
import CloseIcon from '@mui/icons-material/Close';
import UpdateIcon from '@mui/icons-material/Update';
import FolderIcon from '@mui/icons-material/Folder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import RefreshIcon from '@mui/icons-material/Refresh';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import TemplateIcon from '@mui/icons-material/Description';
import FilterListIcon from '@mui/icons-material/FilterList';
import { CalendarMonth as CalendarMonthIcon } from '@mui/icons-material';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import PDFViewer from './PDFViewer';
import FileUploadDialog from './FileUploadDialog';
import { getTreatmentNoteTemplates, getTreatmentNoteTemplate } from '../api/calendarService';

interface TreatmentNote {
  id: string;
  title: string;
  content: string | {
    sections: Array<{
      name: string;
      questions: Array<{
        name: string;
        answer: string;
      }>;
    }>;
  };
  created_at: string;
  author_name?: string;
  draft?: boolean;
}

interface TreatmentNoteTemplate {
  id: string;
  name: string;
  content: {
    sections: Array<{
      name: string;
      description?: string;
      questions: Array<{
        name: string;
        type: string;
      }>;
    }>;
  };
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  print_settings?: {
    include_patient_address: boolean;
    include_patient_dob: boolean;
    include_patient_medicare: boolean;
    include_patient_occupation: boolean;
    include_patient_reference_number: boolean;
    title: string;
  };
}

interface Patient {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  cliniko_id?: string;
}

interface Appointment {
  id: string;
  starts_at: string;
  ends_at: string;
  appointment_type?: any;
  practitioner?: any;
  status: string;
  cancellation_reason?: string;
  
  // Legacy fields that might be present
  appointment_type_id?: string;
  appointment_type_name?: string;
  practitioner_id?: string;
  practitioner_name?: string;
}

interface Attachment {
  id: string;
  filename: string;
  description: string | null;
  content_type: string;
  size: number;
  created_at: string;
  processing_completed: boolean;
  source: 'cliniko';  // All attachments are now from Hydralyfe
  links: {
    self: string;
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper function to parse HTML content and convert it to React elements
const parseHTMLContent = (element: HTMLElement): React.ReactNode => {
  const result: React.ReactNode[] = [];
  
  // Process headings
  const headings = element.querySelectorAll('h1, h2, h3, h4, h5, h6');
  headings.forEach(heading => {
    // Replace the heading with Typography component
    const headingLevel = parseInt(heading.tagName.substring(1));
    result.push(
      <Typography 
        key={`heading-${result.length}`} 
        variant={headingLevel <= 2 ? 'h6' : 'subtitle1'} 
        color="primary.main"
        sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}
      >
        {heading.textContent}
      </Typography>
    );
  });
  
  // Process lists
  const lists = element.querySelectorAll('ul, ol');
  lists.forEach(list => {
    const items = list.querySelectorAll('li');
    const listItems = Array.from(items).map((item, idx) => (
      <Box 
        key={idx} 
        sx={{ 
          py: 0.5, 
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:last-child': { borderBottom: 'none' }
        }}
      >
        <Typography variant="body2">
          {list.tagName === 'OL' ? `${idx + 1}. ` : '• '}
          {item.textContent}
        </Typography>
      </Box>
    ));
    
    if (listItems.length > 0) {
      result.push(
        <Box 
          key={`list-${result.length}`} 
          sx={{ mb: 2 }}
        >
          {listItems}
        </Box>
      );
    }
  });
  
  // Process paragraphs
  const paragraphs = element.querySelectorAll('p');
  paragraphs.forEach(paragraph => {
    if (paragraph.textContent?.trim()) {
      result.push(
        <Typography 
          key={`paragraph-${result.length}`} 
          variant="body1" 
          sx={{ mb: 1 }}
        >
          {paragraph.textContent}
        </Typography>
      );
    }
  });
  
  // If we didn't extract anything specific or there are no special tags,
  // just return the text content as a fallback
  if (result.length === 0 && element.textContent?.trim()) {
    result.push(
      <Typography 
        key="text" 
        variant="body1" 
        sx={{ whiteSpace: 'pre-wrap' }}
      >
        {element.textContent}
      </Typography>
    );
  }
  
  return result;
};

const ClientPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [notes, setNotes] = useState<TreatmentNote[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [selectedTab, setSelectedTab] = useState(0);
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedAttachment, setSelectedAttachment] = useState<Attachment | null>(null);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  
  // Treatment note template states
  const [templates, setTemplates] = useState<TreatmentNoteTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TreatmentNoteTemplate | null>(null);

  // Fetch patient data, notes, and attachments
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');

        // First fetch the patient details to get the Cliniko ID
        const patientResponse = await api.get(`/api/patients/${id}`);
        const patientData = patientResponse.data;
        setPatient(patientData);

        if (patientData.cliniko_id) {
          // Then fetch treatment notes using the Cliniko ID
          const notesResponse = await api.get(`/api/treatment-notes/${patientData.cliniko_id}`, {
            params: {
              sort: 'created_at:desc',
              per_page: 100
            }
          });
          setNotes(notesResponse.data.treatment_notes || []);
        }

        // Fetch attachments
        const attachmentsResponse = await api.get(`/api/patient-attachments/patient/${id}`);
        setAttachments(attachmentsResponse.data.patient_attachments || []);
        
        // Fetch future appointments if we have a Cliniko ID
        if (patientData.cliniko_id) {
          await fetchAppointments(patientData.cliniko_id);
        }
      } catch (err: any) {
        console.error('Error fetching client data:', err);
        setError(err.response?.data?.error || 'Failed to fetch client data');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchData();
    }
  }, [id]);
  
  // Function to fetch patient's appointments
  const fetchAppointments = async (clinikoId: string) => {
    try {
      setAppointmentsLoading(true);
      
      // Get today's date in ISO format
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayIso = today.toISOString();
      
      // Set end date 6 months from now
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 6);
      const endDateIso = endDate.toISOString();
      
      // Fetch future appointments for this patient
      const response = await api.get('/api/appointments', {
        params: {
          patient_id: clinikoId,
          starts_at_gteq: todayIso,
          ends_at_lteq: endDateIso,
          sort: 'starts_at:asc'
        }
      });
      
      console.log('Appointments API response:', response.data);
      
      // Transform the data to match our expected interface structure
      const transformedAppointments = (response.data.appointments || []).map((appointment: any) => {
        console.log('Raw appointment data:', appointment);
        
        // Extract appointment type and name from various possible locations
        let appointmentTypeName = 'Unknown Appointment';
        if (appointment.appointment_type && appointment.appointment_type.name) {
          appointmentTypeName = appointment.appointment_type.name;
        } else if (appointment.appointment_type_name) {
          appointmentTypeName = appointment.appointment_type_name;
        } else if (appointment.appointment_category) {
          appointmentTypeName = appointment.appointment_category;
        } else if (appointment.type_name) {
          appointmentTypeName = appointment.type_name;
        }
        
        // Extract practitioner information
        let practitionerName = 'Unknown Practitioner';
        if (appointment.practitioner && appointment.practitioner.name) {
          practitionerName = appointment.practitioner.name;
        } else if (appointment.practitioner_name) {
          practitionerName = appointment.practitioner_name;
        } else if (appointment.practitioner_id) {
          // If we only have ID, try to format it nicely
          practitionerName = `Practitioner #${appointment.practitioner_id.substring(0, 6)}`;
        } 
        
        // If we have first/last name separately
        else if (appointment.practitioner && 
                (appointment.practitioner.first_name || appointment.practitioner.last_name)) {
          const firstName = appointment.practitioner.first_name || '';
          const lastName = appointment.practitioner.last_name || '';
          practitionerName = `${firstName} ${lastName}`.trim();
        }
        
        return {
          id: appointment.id,
          starts_at: appointment.starts_at || appointment.appointment_start || new Date().toISOString(),
          ends_at: appointment.ends_at || appointment.appointment_end || new Date().toISOString(),
          status: appointment.status || 'booked',
          cancellation_reason: appointment.cancellation_reason,
          // Use our extracted values
          appointment_type: {
            id: appointment.appointment_type?.id || appointment.appointment_type_id || '0',
            name: appointmentTypeName
          },
          practitioner: {
            id: appointment.practitioner?.id || appointment.practitioner_id || '0',
            name: practitionerName
          }
        };
      });
      
      console.log('Transformed appointments:', transformedAppointments);
      
      // Set appointments initially with basic data first
      setAppointments(transformedAppointments);
      
      // Fetch actual practitioner and appointment type details directly
      const fetchPractitionerAndTypeDetails = async () => {
        try {
          console.log('Fetching detailed practitioner and appointment type data...');
          setAppointmentsLoading(true);
          
          // 1. Create a list of all unique practitioner IDs we need to fetch
          const practitionerIds = [...new Set(
            transformedAppointments
              .map(apt => apt.practitioner.id)
              .filter(id => id && id !== '0')
          )];
          
          // 2. Create a list of all unique appointment type IDs we need to fetch
          const appointmentTypeIds = [...new Set(
            transformedAppointments
              .map(apt => apt.appointment_type.id)
              .filter(id => id && id !== '0')
          )];
          
          console.log('Practitioner IDs to fetch:', practitionerIds);
          console.log('Appointment type IDs to fetch:', appointmentTypeIds);
          
          // 3. Fetch all practitioners in one call if possible
          let practitionersMap: Record<string, { name: string, id: string }> = {};
          if (practitionerIds.length > 0) {
            try {
              const practitionersResponse = await api.get('/api/cliniko/practitioners');
              console.log('Practitioners response:', practitionersResponse.data);
              
              if (practitionersResponse.data && practitionersResponse.data.practitioners) {
                // Create a map of ID -> practitioner details
                practitionersMap = practitionersResponse.data.practitioners.reduce(
                  (map: any, p: any) => {
                    map[p.id] = { 
                      name: `${p.first_name} ${p.last_name}`.trim(),
                      id: p.id
                    };
                    return map;
                  }, 
                  {}
                );
              }
            } catch (err) {
              console.error('Error fetching practitioners:', err);
            }
          }
          
          // 4. Fetch all appointment types in one call
          let appointmentTypesMap: Record<string, { name: string, id: string }> = {};
          try {
            const typesResponse = await api.get('/api/cliniko/appointment_types');
            console.log('Appointment types response:', typesResponse.data);
            
            if (typesResponse.data && typesResponse.data.appointment_types) {
              // Create a map of ID -> appointment type details
              appointmentTypesMap = typesResponse.data.appointment_types.reduce(
                (map: any, type: any) => {
                  map[type.id] = { 
                    name: type.name,
                    id: type.id
                  };
                  return map;
                }, 
                {}
              );
            }
          } catch (err) {
            console.error('Error fetching appointment types:', err);
          }
          
          console.log('Practitioners map:', practitionersMap);
          console.log('Appointment types map:', appointmentTypesMap);
          
          // 5. Enrich our appointments with the fetched data
          const enrichedAppointments = transformedAppointments.map(apt => {
            const enrichedAppointment = { ...apt };
            
            // Update practitioner name if we found it
            if (practitionersMap[apt.practitioner.id]) {
              enrichedAppointment.practitioner = {
                ...apt.practitioner,
                name: practitionersMap[apt.practitioner.id].name
              };
            }
            
            // Update appointment type name if we found it
            if (appointmentTypesMap[apt.appointment_type.id]) {
              enrichedAppointment.appointment_type = {
                ...apt.appointment_type,
                name: appointmentTypesMap[apt.appointment_type.id].name
              };
            }
            
            return enrichedAppointment;
          });
          
          console.log('Final enriched appointments:', enrichedAppointments);
          setAppointments(enrichedAppointments);
        } catch (err) {
          console.error('Error during appointment enrichment:', err);
        } finally {
          setAppointmentsLoading(false);
        }
      };
      
      // Start the enrichment process 
      fetchPractitionerAndTypeDetails();
    } catch (err) {
      console.error('Error fetching patient appointments:', err);
    } finally {
      setAppointmentsLoading(false);
    }
  };
  
  // Fetch treatment note templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setTemplatesLoading(true);
        const response = await getTreatmentNoteTemplates();
        console.log('Templates response:', response);
        
        // Filter for the IV Therapy appointment template if requested
        const ivTemplates = response.treatment_note_templates.filter(
          (template: TreatmentNoteTemplate) => 
            template.name.toLowerCase().includes('iv therapy')
        );
        
        if (ivTemplates.length > 0) {
          setTemplates(ivTemplates);
        } else {
          // If IV template not found, use all templates
          setTemplates(response.treatment_note_templates);
        }
      } catch (err) {
        console.error('Error fetching treatment note templates:', err);
      } finally {
        setTemplatesLoading(false);
      }
    };
    
    fetchTemplates();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleDownload = async (attachment: Attachment) => {
    try {
      console.log('Downloading attachment:', attachment);
      
      // Get the ID from the attachment's links.self URL
      const selfUrl = attachment.links.self;
      const attachmentId = selfUrl.split('/').pop();
      
      // Check if the attachment is processed
      if (!attachment.processing_completed) {
        alert('This attachment is still being processed. Please try again later.');
        return;
      }

      // Create a link element and trigger download
      const link = document.createElement('a');
      
      // All attachments are now from Hydralyfe
      link.href = `/api/cliniko-attachments/${attachmentId}`;
      
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      alert('Failed to download attachment');
    }
  };

  const handleViewAttachment = async (attachment: Attachment) => {
    try {
      console.log('Opening attachment in modal:', attachment.filename);
      // Get the ID from the attachment's links.self URL
      const selfUrl = attachment.links.self;
      const attachmentId = selfUrl.split('/').pop();
      
      // Check if the attachment is processed
      if (!attachment.processing_completed) {
        alert('This attachment is still being processed. Please try again later.');
        return;
      }

      // Set the selected attachment and open the viewer
      setSelectedAttachment({
        ...attachment,
        // All attachments are now from Hydralyfe
        source: 'cliniko' 
      });
      setPdfViewerOpen(true);
    } catch (err) {
      console.error('Error viewing attachment:', err);
      alert('Failed to view attachment');
    }
  };

  const handleCloseViewer = () => {
    setPdfViewerOpen(false);
    setSelectedAttachment(null);
  };

  // Handle template selection
  const handleTemplateChange = async (event: SelectChangeEvent<string>) => {
    const templateId = event.target.value;
    setSelectedTemplateId(templateId);
    
    if (!templateId) {
      setSelectedTemplate(null);
      return;
    }
    
    try {
      const template = await getTreatmentNoteTemplate(templateId);
      setSelectedTemplate(template);
      
      // Auto-fill the title field with the template name
      setNewTitle(template.name || 'IV Therapy Note');
    } catch (error) {
      console.error('Error fetching template details:', error);
    }
  };
  
  // Handle creating draft from template
  const handleUseTemplate = () => {
    if (!selectedTemplate) return;
    
    // Create empty form content from template structure
    const formContent = selectedTemplate.content.sections.map(section => {
      return {
        name: section.name,
        questions: section.questions.map(question => ({
          name: question.name,
          answer: ''  // Start with empty answers
        }))
      };
    });
    
    // Show the form with template data
    setShowTemplateForm(true);
  };
  
  // Handle regular note submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const payload = {
        patient_id: patient?.cliniko_id,
        title: newTitle,
        content: newContent
      };

      const response = await api.post('/api/treatment-notes', payload);

      // Add the new note to the list
      setNotes(prev => [response.data, ...prev]);
      
      // Clear the form and hide it
      setNewTitle('');
      setNewContent('');
      setShowForm(false);

      // Show success message
      setSuccessMessage('Treatment note created successfully');
    } catch (err: any) {
      console.error('Error creating treatment note:', err);
      setError(err.response?.data?.error || 'Failed to create treatment note');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle template-based note submission
  const handleSubmitTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Get the form data
      const formData = new FormData(e.target as HTMLFormElement);
      const formAnswers: Record<string, string> = {};
      
      // Convert form data to answers object
      for (const [key, value] of formData.entries()) {
        formAnswers[key] = value as string;
      }
      
      if (!selectedTemplate) {
        throw new Error('No template selected');
      }
      
      // Format the form data for display
      const formFields: Record<string, string> = {};
      
      // For each question in the template, add the answer to the form fields
      // using plain "question: answer" format
      selectedTemplate.content.sections.forEach(section => {
        section.questions.forEach(question => {
          const formKey = `${section.name}__${question.name}`;
          const answer = formAnswers[formKey] || '';
          
          // Store as "question: answer" for display
          formFields[question.name] = answer;
        });
      });
      
      // Create a simple string for the note content
      // Each line is "Question: Answer"
      const plainTextContent = selectedTemplate.content.sections.map(section => {
        // First add the section header
        let sectionText = `${section.name}\n\n`;
        
        // Then add each question and answer
        section.questions.forEach(question => {
          const formKey = `${section.name}__${question.name}`;
          const answer = formAnswers[formKey] || 'No response';
          
          sectionText += `${question.name}: ${answer}\n`;
        });
        
        return sectionText;
      }).join('\n\n');
      
      // Create payload using the template_id approach
      // This way Cliniko will use the template formatting directly
      const payload = {
        patient_id: patient?.cliniko_id,
        title: newTitle || selectedTemplate?.name || 'IV Therapy Note',
        treatment_note_template_id: selectedTemplateId,
        content: {
          sections: selectedTemplate.content.sections.map(section => ({
            name: section.name,
            description: section.description || '',
            questions: section.questions.map(question => {
              const formKey = `${section.name}__${question.name}`;
              return {
                name: question.name,
                type: question.type,
                answer: formAnswers[formKey] || ''
              };
            })
          }))
        }
      };

      const response = await api.post('/api/treatment-notes', payload);

      // Add the new note to the list
      setNotes(prev => [response.data, ...prev]);
      
      // Clear the form and hide it
      setNewTitle('');
      setSelectedTemplateId('');
      setSelectedTemplate(null);
      setShowTemplateForm(false);

      // Show success message
      setSuccessMessage('Treatment note created successfully from template');
    } catch (err: any) {
      console.error('Error creating treatment note from template:', err);
      setError(err.response?.data?.error || 'Failed to create treatment note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadComplete = async (data?: any) => {
    if (data && data.attachment) {
      // If we have data from upload response, add it directly to the attachments list
      const newAttachment: Attachment = {
        id: data.attachment.id,
        filename: data.attachment.filename,
        description: null,
        content_type: 'application/octet-stream', // Default, will be updated when processed
        size: 0, // Will be updated when processed
        created_at: new Date().toISOString(),
        processing_completed: data.attachment.processing_completed,
        source: 'cliniko',
        links: {
          self: `/patient_attachments/${data.attachment.id}`
        }
      };
      
      // Add to beginning of list
      setAttachments(prev => [newAttachment, ...prev]);
    } else {
      // Fallback to refreshing from API
      try {
        const attachmentsResponse = await api.get(`/api/patient-attachments/patient/${id}`);
        setAttachments(attachmentsResponse.data.patient_attachments || []);
      } catch (err) {
        console.error('Error refreshing attachments:', err);
      }
    }
  };

  // Helper function to format appointment dates
  const formatAppointmentDate = (dateString: string) => {
    const date = new Date(dateString);
    
    const dateFormat = new Intl.DateTimeFormat('en-AU', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    
    const timeFormat = new Intl.DateTimeFormat('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    return {
      date: dateFormat.format(date),
      time: timeFormat.format(date)
    };
  };

  // Function to render appointments
  const renderAppointments = () => {
    if (appointmentsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    
    if (!appointments.length) {
      return (
        <Box sx={{ textAlign: 'center', p: 2 }}>
          <Typography color="text.secondary">No upcoming appointments</Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        maxHeight: '250px',
        overflowY: 'auto',
        bgcolor: 'background.paper',
        borderRadius: 1,
      }}>
        {appointments.map((appointment, index) => {
          const { date, time } = formatAppointmentDate(appointment.starts_at);
          
          // Determine status color
          let statusColor = 'primary.main';
          if (appointment.status === 'cancelled') {
            statusColor = 'error.main';
          } else if (appointment.status === 'did_not_arrive') {
            statusColor = 'warning.main';
          }
          
          return (
            <Box
              key={appointment.id}
              sx={{
                p: 1.5,
                borderBottom: index < appointments.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'flex-start',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <Box sx={{ minWidth: '160px' }}>
                <Typography variant="subtitle2" color="primary.main">
                  {date}
                </Typography>
                <Typography variant="body2">{time}</Typography>
              </Box>
              
              <Box sx={{ flex: 1, minWidth: '150px' }}>
                <Typography variant="subtitle2">
                  {appointment.appointment_type?.name || appointment.appointment_type_name || 'Appointment'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  with {appointment.practitioner?.name || appointment.practitioner_name || 'Practitioner'}
                </Typography>
              </Box>
              
              <Chip 
                label={appointment.status.replace('_', ' ')}
                size="small"
                sx={{
                  textTransform: 'capitalize',
                  color: statusColor,
                  borderColor: statusColor,
                  bgcolor: 'transparent',
                  border: '1px solid'
                }}
              />
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderOverview = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" color="primary">Patient Information</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ minWidth: 120 }} color="textSecondary">Name:</Typography>
                  <Typography>{patient?.first_name} {patient?.last_name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ minWidth: 120 }} color="textSecondary">Email:</Typography>
                  <Typography>{patient?.email}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ minWidth: 120 }} color="textSecondary">Hydralyfe ID:</Typography>
                  <Typography>{patient?.cliniko_id || 'Not linked'}</Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Upcoming Appointments</Typography>
                  {renderAppointments()}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', bgcolor: 'primary.light' }}>
            <CardContent>
              <Typography variant="h6" color="white" gutterBottom>Quick Stats</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <NoteIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="overline" color="textSecondary">Treatment Notes</Typography>
                  </Box>
                  <Typography variant="h4">{notes.length}</Typography>
                </Box>
                <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <UpdateIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="overline" color="textSecondary">Latest Note</Typography>
                  </Box>
                  <Typography variant="body1">
                    {notes[0]?.created_at 
                      ? new Date(notes[0].created_at).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })
                      : 'No notes available'}
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="overline" color="textSecondary">Attachments</Typography>
                  </Box>
                  <Typography variant="h4">{attachments.length}</Typography>
                </Box>
                <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CalendarMonthIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="overline" color="textSecondary">Upcoming Appointments</Typography>
                  </Box>
                  <Typography variant="h4">{appointments.length}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderTreatmentNotes = () => {
    return (
      <>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          mb: 2 
        }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<TemplateIcon />}
              onClick={() => {
                setShowTemplateForm(!showTemplateForm);
                setShowForm(false);
              }}
              color={showTemplateForm ? 'secondary' : 'primary'}
            >
              {showTemplateForm ? 'Cancel Template' : 'Use IV Template'}
            </Button>
          </Box>
          
          <Button
            variant="contained"
            startIcon={<EventNoteIcon />}
            onClick={() => {
              setShowForm(!showForm);
              setShowTemplateForm(false);
            }}
            color={showForm ? 'secondary' : 'primary'}
          >
            {showForm ? 'Cancel' : 'New Note'}
          </Button>
        </Box>

        {/* Standard free-form note creation */}
        <Collapse in={showForm} sx={{ mb: 3 }}>
          <Card variant="outlined">
            <CardContent>
              <Box component="form" onSubmit={handleSubmit}>
                <Typography variant="h6" gutterBottom>
                  Create New Treatment Note
                </Typography>
                <TextField
                  fullWidth
                  label="Title"
                  value={newTitle}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTitle(e.target.value)}
                  disabled={submitting}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Content"
                  value={newContent}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewContent(e.target.value)}
                  disabled={submitting}
                  sx={{ mb: 2 }}
                />
                <Button 
                  type="submit" 
                  variant="contained" 
                  disabled={submitting || !newTitle || !newContent}
                  sx={{ mr: 1 }}
                >
                  {submitting ? 'Saving...' : 'Save Note'}
                </Button>
                <Button 
                  type="button"
                  variant="outlined"
                  disabled={submitting}
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Collapse>
        
        {/* Template-based note creation */}
        <Collapse in={showTemplateForm} sx={{ mb: 3 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Create Note from Template
              </Typography>
              
              {templatesLoading ? (
                <CircularProgress size={24} sx={{ ml: 2 }} />
              ) : templates.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No treatment note templates found
                </Alert>
              ) : (
                <>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="template-select-label">Template</InputLabel>
                    <Select
                      labelId="template-select-label"
                      id="template-select"
                      value={selectedTemplateId}
                      label="Template"
                      onChange={handleTemplateChange}
                      disabled={submitting}
                    >
                      <MenuItem value="">
                        <em>Select a template</em>
                      </MenuItem>
                      {templates.map((template) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Select an IV Therapy template</FormHelperText>
                  </FormControl>
                  
                  {selectedTemplate ? (
                    <>
                      <TextField
                        fullWidth
                        label="Note Title"
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        disabled={submitting}
                        sx={{ mb: 2 }}
                      />
                      
                      <Box component="form" id="template-form" onSubmit={handleSubmitTemplate}>
                        {selectedTemplate.content.sections.map((section, sIdx) => (
                          <Box key={sIdx} sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                              {section.name}
                            </Typography>
                            {section.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {section.description}
                              </Typography>
                            )}
                            
                            <Box sx={{ pl: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {section.questions.map((question, qIdx) => (
                                <TextField
                                  key={qIdx}
                                  name={`${section.name}__${question.name}`}
                                  label={question.name}
                                  fullWidth
                                  multiline={question.type === 'text'}
                                  rows={question.type === 'text' ? 3 : 1}
                                  variant="outlined"
                                  size="small"
                                  helperText={question.type || "text"}
                                />
                              ))}
                            </Box>
                          </Box>
                        ))}
                        
                        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                          <Button
                            type="submit"
                            variant="contained"
                            disabled={submitting}
                          >
                            {submitting ? 'Saving...' : 'Save Note From Template'}
                          </Button>
                          <Button
                            onClick={() => {
                              setShowTemplateForm(false);
                              setSelectedTemplateId('');
                              setSelectedTemplate(null);
                            }}
                            variant="outlined"
                            disabled={submitting}
                          >
                            Cancel
                          </Button>
                        </Box>
                      </Box>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      disabled={!selectedTemplateId || submitting}
                      onClick={handleUseTemplate}
                      sx={{ mt: 1 }}
                    >
                      Load Template
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Collapse>

        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={() => setSuccessMessage(null)}
          message={successMessage}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => setSuccessMessage(null)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />

        {notes.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default' }}>
            <EventNoteIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No treatment notes found
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {notes.map(note => (
              <Accordion 
                key={note.id}
                TransitionComponent={Collapse}
                sx={{ 
                  '&:before': { display: 'none' },
                  boxShadow: 2,
                  borderRadius: '8px !important',
                  overflow: 'hidden',
                  bgcolor: note.draft ? 'action.hover' : 'background.paper',
                  '& .MuiAccordionSummary-root': {
                    borderRadius: '8px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 2
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EventNoteIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      {note.title}
                    </Typography>
                    {note.draft && (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        bgcolor: 'warning.main',
                        color: 'warning.contrastText',
                        borderRadius: '4px',
                        px: 1,
                        py: 0.25
                      }}>
                        <DraftsIcon sx={{ fontSize: '0.875rem', mr: 0.5 }} />
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                          Draft
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {note.author_name && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonOutlineIcon sx={{ fontSize: '0.875rem', mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {note.author_name}
                        </Typography>
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {new Date(note.created_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Divider sx={{ mb: 2 }} />
                  {renderNoteContent(note)}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </>
    );
  };

  const renderNoteContent = (note: TreatmentNote) => {
    if (typeof note.content === 'string') {
      // Display content with proper line breaks
      return (
        <Box sx={{ px: { xs: 0, sm: 1 } }}>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {note.content}
          </Typography>
        </Box>
      );
    }

    // Handle structured content (sections/questions format)
    if (note.content && typeof note.content === 'object' && note.content.sections) {
      return (
        <Box sx={{ px: 1 }}>
          {note.content.sections.map((section, idx) => (
            <Box key={idx} sx={{ mb: 2 }}>
              {section.name && (
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    color: 'primary.main',
                    fontWeight: 600,
                    mb: 1
                  }}
                >
                  {section.name}
                </Typography>
              )}
              <Box sx={{ pl: 2 }}>
                {section.questions?.map((question, qIdx) => (
                  <Box 
                    key={qIdx} 
                    sx={{ 
                      display: 'flex',
                      mb: 0.5,
                      '&:last-child': { mb: 0 }
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        minWidth: '180px',
                        pr: 2
                      }}
                    >
                      {question.name}:
                    </Typography>
                    <Typography variant="body2">
                      {question.answer || 'Not specified'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      );
    }

    // Fallback for plain text content
    return (
      <Box sx={{ px: 1 }}>
        <Typography variant="body2">
          {typeof note.content === 'string' ? note.content : 'No content available'}
        </Typography>
      </Box>
    );
  };

  const renderAttachments = (): JSX.Element => {
    return (
      <>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          mb: 2 
        }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleUploadComplete}
          >
            Refresh Attachments
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload Attachment
          </Button>
        </Box>

        {!attachments.length ? (
          <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default' }}>
            <NoteIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No attachments found
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {attachments.map((attachment) => (
              <Accordion 
                key={attachment.id}
                TransitionComponent={Collapse}
                sx={{ 
                  '&:before': { display: 'none' },
                  boxShadow: 3,
                  borderRadius: '8px !important',
                  overflow: 'hidden',
                  '& .MuiAccordionSummary-root': {
                    borderRadius: '8px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 2,
                      width: '100%'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                    <NoteIcon sx={{ color: 'primary.main', flexShrink: 0 }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {attachment.filename || 'Processing...'}
                      <Chip 
                        size="small" 
                        color="primary" 
                        label="Hydralyfe" 
                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                      />
                    </Typography>
                    {!attachment.processing_completed && (
                      <Chip 
                        size="small" 
                        color="warning" 
                        label="Processing" 
                        icon={<HourglassEmptyIcon />} 
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'flex-end',
                    minWidth: 'fit-content'
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(attachment.created_at).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {attachment.size ? formatFileSize(attachment.size) : 'Size unknown'}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ pl: 1 }}>
                    {attachment.description && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ mb: 2, fontStyle: 'italic' }}
                      >
                        {attachment.description}
                      </Typography>
                    )}
                    {!attachment.processing_completed ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                          This attachment is still being processed by Hydralyfe. Please check back later.
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewAttachment(attachment)}
                          size="small"
                        >
                          View
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownload(attachment)}
                          size="small"
                        >
                          Download
                        </Button>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        <FileUploadDialog
          open={uploadDialogOpen}
          onClose={() => setUploadDialogOpen(false)}
          patientId={id || ''}
          onUploadComplete={handleUploadComplete}
        />
      </>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          onClick={() => navigate('/')}
          variant="contained"
          sx={{ mb: 2 }}
        >
          Back to List
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      maxWidth: '100%', 
      p: { xs: 1, sm: 2, md: 3 }, 
      overflowX: 'hidden'
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ wordBreak: 'break-word' }}>
          {patient?.first_name} {patient?.last_name}
        </Typography>
        <Button
          onClick={() => navigate('/')}
          variant="contained"
        >
          Back to List
        </Button>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={selectedTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ minHeight: 48 }}
        >
          <Tab label="Overview" />
          <Tab label="Patient Information" />
          <Tab label="Appointments" />
          <Tab label="Treatment Notes" />
          <Tab label="Attachments" />
        </Tabs>
      </Paper>

      <TabPanel value={selectedTab} index={0}>
        {renderOverview()}
      </TabPanel>
      <TabPanel value={selectedTab} index={1}>
        <PatientEditForm patientId={id || ''} />
      </TabPanel>
      <TabPanel value={selectedTab} index={2}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>Upcoming Appointments</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Showing appointments for the next 6 months
          </Typography>
          
          <Card variant="outlined">
            <CardContent>
              {appointmentsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : appointments.length === 0 ? (
                <Box sx={{ textAlign: 'center', p: 4 }}>
                  <CalendarMonthIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No upcoming appointments found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    This patient has no scheduled appointments in the next 6 months.
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {appointments.map((appointment, index) => {
                    const { date, time } = formatAppointmentDate(appointment.starts_at);
                    const endTime = formatAppointmentDate(appointment.ends_at).time;
                    
                    // Determine status color and icon
                    let statusColor = 'success.main';
                    let statusBgColor = 'success.light';
                    
                    if (appointment.status === 'cancelled') {
                      statusColor = 'error.main';
                      statusBgColor = 'error.light';
                    } else if (appointment.status === 'did_not_arrive') {
                      statusColor = 'warning.main';
                      statusBgColor = 'warning.light';
                    }
                    
                    return (
                      <Box
                        key={appointment.id}
                        sx={{
                          mb: 2,
                          p: 2,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: index % 2 === 0 ? 'background.default' : 'background.paper',
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                              {date}
                            </Typography>
                            <Typography variant="body2">
                              {time} - {endTime}
                            </Typography>
                            <Chip 
                              label={appointment.status.replace('_', ' ')}
                              size="small"
                              sx={{
                                mt: 1,
                                textTransform: 'capitalize',
                                color: statusColor,
                                bgcolor: statusBgColor,
                                fontWeight: 'bold'
                              }}
                            />
                          </Grid>
                          
                          <Grid item xs={12} sm={9}>
                            <Typography variant="h6">
                              {appointment.appointment_type?.name || appointment.appointment_type_name || 'Appointment'}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              with {appointment.practitioner?.name || appointment.practitioner_name || 'Practitioner'}
                            </Typography>
                            
                            {appointment.status === 'cancelled' && appointment.cancellation_reason && (
                              <Box sx={{ mt: 1, p: 1, bgcolor: 'error.light', borderRadius: 1 }}>
                                <Typography variant="body2" color="error.dark">
                                  Cancellation reason: {appointment.cancellation_reason}
                                </Typography>
                              </Box>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </TabPanel>
      <TabPanel value={selectedTab} index={3}>
        {renderTreatmentNotes()}
      </TabPanel>
      <TabPanel value={selectedTab} index={4}>
        {renderAttachments()}
      </TabPanel>

      {selectedAttachment && (
        <PDFViewer
          open={pdfViewerOpen}
          onClose={handleCloseViewer}
          fileUrl={`/api/cliniko-attachments/${selectedAttachment.links.self.split('/').pop()}`}
        />
      )}
    </Box>
  );
};

export default ClientPage; 